const DeviceType = {
  TYPE_UNSET: 0,
  TYPE_WIN: 1,
  TYPE_MAC: 2,
  TYPE_LINUX: 3,
  TYPE_CROS: 4,
  TYPE_OTHER: 5,
  TYPE_PHONE: 6,
  TYPE_TABLET: 7,
};

export const PlatformType = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
  TABLET: 'tablet',
  OTHER: 'other',
};

const DeviceToPlatform = {
  DESKTOP: [DeviceType.TYPE_WIN, DeviceType.TYPE_MAC, DeviceType.TYPE_LINUX],
  MOBILE: [DeviceType.TYPE_PHONE],
  TABLET: [DeviceType.TYPE_TABLET],
};

export const FolderType = {
  USER_FOLDER: 0,
  BOOKMARK_BAR: 1,
  USER_BOOKMARKS: 2,
  TRASH: 3,
  UNSORTED_BOOKMARKS: 4,
};

function ldapToJS(n) {
  // Longer, equivalent to short version
  // return new Date(n*10/1e4 + new Date(Date.UTC(1601,0,1)).getTime());

  // Shorter, more efficient. Uses time value for 1601-01-01 UTC
  return new Date(n / 1e3 - 1.16444736e13);
}

class Conversion {
  static isWindow = (entity) => (entity.specifics.session && entity.specifics.session.header);

  static parseWindow = (entity) => ({
    id: entity.idString,
    name: entity.specifics.session.header.clientName,
    deviceType: entity.specifics.session.header.deviceType,
    windowIds: entity.specifics.session.header.window.map((w) => w.windowId),
    tabIds: entity.specifics.session.header.window.map((w) => w.tab).flat(),
  });

  static isRemovedTab = (entity) => (entity.deleted && entity.specifics.session);

  static isTab = (entity) => (entity.specifics.session
                              && (entity.specifics.session.tab || entity.specifics.encrypted));

  static parseTab = (entity) => {
    const tab = {
      id: entity.idString,
      encrypted: Boolean(entity.specifics.encrypted),
      title: null,
      url: null,
      favicon: null,
      tabId: null,
      windowId: null,
    };
    if (!tab.encrypted) {
      const page = entity.specifics.session.tab.navigation.slice(-1).pop();
      tab.title = page.title;
      tab.url = page.virtualUrl;
      tab.favicon = page.faviconUrl;
      tab.tabId = entity.specifics.session.tab.tabId;
      tab.windowId = entity.specifics.session.tab.windowId;
    }
    return tab;
  };

  static isRemovedHistory = (entity) => (entity.deleted && entity.specifics.typedUrl);

  static isHistory = (entity) => (entity.specifics.typedUrl && entity.serverDefinedUniqueTag !== 'google_chrome_typed_urls');

  static parseHistory = (entity) => {
    const history = {
      id: entity.idString,
      encrypted: Boolean(entity.specifics.encrypted),
      title: null,
      url: null,
      visited: 0,
    };
    if (!history.encrypted) {
      history.title = entity.specifics.typedUrl.title;
      history.url = entity.specifics.typedUrl.url;
      // in order to show history like in actual browser we need to squash redirect chains together
      // http://facebook.com -> https://facebook.com -> https://www.facebook.com
      // should be visible as just https://www.facebook.com
      // for this we cut protocol, www. and trailing slash to see if url is the same
      const url = new URL(history.url);
      // cut protocol from url
      history.normal_url = history.url.slice(url.protocol.length + 2);
      // cut leading www part from url
      if (history.normal_url.startsWith('www.')) {
        history.normal_url = history.normal_url.slice('www.'.length);
      }
      // cut trailing slash from url
      if (history.normal_url.endsWith('/')) {
        history.normal_url = history.normal_url.slice(0, -1);
      }
      history.visited = ldapToJS(entity.specifics.typedUrl.visits.slice(-1).pop());
      history.visited.setMilliseconds(0);
    }
    return history;
  };

  static isRemovedDeviceInfo = (entity) => (entity.deleted && entity.specifics.deviceInfo);

  static isDeviceInfo = (entity) => (entity.specifics.deviceInfo && entity.serverDefinedUniqueTag !== 'google_chrome_device_info');

  static parseDeviceInfo = (entity) => {
    const res = {
      id: entity.idString,
      name: entity.specifics.deviceInfo.clientName,
    };
    const type = entity.specifics.deviceInfo.deviceType;
    if (DeviceToPlatform.DESKTOP.includes(type)) {
      res.platform = PlatformType.DESKTOP;
    } else if (DeviceToPlatform.MOBILE.includes(type)) {
      res.platform = PlatformType.MOBILE;
    } else if (DeviceToPlatform.TABLET.includes(type)) {
      res.platform = PlatformType.TABLET;
    } else {
      res.platform = PlatformType.OTHER;
    }
    return res;
  };

  static isRemovedPreference = (entity) => (entity.deleted && entity.specifics.preference);

  static isPreference = (entity) => (entity.specifics.preference && entity.serverDefinedUniqueTag !== 'google_chrome_preferences');

  static parsePreference = (entity) => ({ id: entity.idString });

  static isRemovedPassword = (entity) => (entity.deleted && entity.specifics.password);

  static isPassword = (entity) => (entity.specifics.password && entity.serverDefinedUniqueTag !== 'google_chrome_passwords');

  static parsePassword = (entity) => ({ id: entity.idString });

  static isRemovedBookmark = (entity) => (entity.deleted && entity.specifics.bookmark);

  static isBookmark = (entity) => (entity.specifics.bookmark);

  static parseBookmark = (entity) => {
    const bookmark = {
      id: entity.idString,
      encrypted: Boolean(entity.specifics.encrypted),
      parent: entity.parentIdString,
      folder: entity.folder ? 1 : 0,
      type: entity.serverDefinedUniqueTag,
      title: null,
      url: null,
      favicon: null,
    };
    if (bookmark.folder === 1) {
      switch (bookmark.type) {
        case 'bookmark_bar':
          bookmark.folder_type = FolderType.BOOKMARK_BAR;
          break;
        case 'user_root_bookmarks':
          bookmark.folder_type = FolderType.USER_BOOKMARKS;
          break;
        case 'trash_bookmarks':
          bookmark.folder_type = FolderType.TRASH;
          break;
        case 'unsorted_bookmarks':
          bookmark.folder_type = FolderType.UNSORTED_BOOKMARKS;
          break;
        default:
          bookmark.folder_type = FolderType.USER_FOLDER;
      }
    }
    if (!bookmark.encrypted) {
      bookmark.title = entity.name;
      bookmark.url = entity.specifics.bookmark.url;
      bookmark.favicon = entity.specifics.bookmark.iconUrl;
    }

    return bookmark;
  };
}

export default Conversion;

export const serializeProgressMarkers = (markers) => {
  const res = Object.keys(markers).reduce(
    (map, k) => { map[k] = Array.from(markers[k]); return map; },
    {},
  );
  return JSON.stringify(res);
};

export const parseProgressMarkers = (data) => {
  const raw = JSON.parse(data);
  const res = Object.keys(raw).reduce(
    (map, k) => { map[k] = new Uint8Array(raw[k]); return map; },
    {},
  );
  return res;
};
